//
// # SelectionBoxEntity
//
// A selection box on a symbol. Has its own EntityKind so the change pipeline
// routes it to the "selectionbox" target instead of "graphics".
// Standalone from RectEntity to avoid polluting the graphics primitive hierarchy.
//

import { graphics, Point, Bbox, LineSegment } from "GenAlphaModel";
import { EntityKind } from "../../entity";
import { MousePos } from "../../mousepos";
import { DrawTo } from "../../draw";
import { ShapeBase } from "../../tree";

const EDGE_HIT_TOLERANCE = 5;

export class SelectionBoxEntity extends ShapeBase<graphics.Rectangle> {
  readonly entityKind = EntityKind.SelectionBox;

  override redraw = (to: DrawTo): unknown => {
    return to.rect(this.data, this.style);
  };

  get corners(): [
    SelectionBoxCorner,
    SelectionBoxCorner,
    SelectionBoxCorner,
    SelectionBoxCorner
  ] {
    const { bbox } = this.data;
    const opposites = [
      [bbox.min, bbox.max],
      [
        { x: bbox.min.x, y: bbox.max.y },
        { x: bbox.max.x, y: bbox.min.y },
      ],
    ];
    return [
      new SelectionBoxCorner(this, opposites[0][0], opposites[0][1]),
      new SelectionBoxCorner(this, opposites[0][1], opposites[0][0]),
      new SelectionBoxCorner(this, opposites[1][0], opposites[1][1]),
      new SelectionBoxCorner(this, opposites[1][1], opposites[1][0]),
    ];
  }

  // Current center of the selection box.
  get loc(): Point {
    return Bbox.sizeAndCenter(this.data.bbox).center;
  }

  // Translate the selection box to a new center location.
  updateLoc = (loc: Point): unknown => {
    const { size } = Bbox.sizeAndCenter(this.data.bbox);
    return this.update({ bbox: Bbox.fromSizeAndCenter(size, loc) });
  };

  // Hit test against the four edges (for whole-shape movement).
  edgeHitTest = (mousePos: MousePos): boolean => {
    const { bbox } = this.data;
    const corners = [
      bbox.min,
      { x: bbox.max.x, y: bbox.min.y },
      bbox.max,
      { x: bbox.min.x, y: bbox.max.y },
    ];
    for (let i = 0; i < corners.length; i++) {
      const next = corners[(i + 1) % corners.length];
      const seg: LineSegment = { ends: [corners[i], next] };
      if (LineSegment.closest(mousePos.sch, seg).dist < EDGE_HIT_TOLERANCE) {
        return true;
      }
    }
    return false;
  };
}

// Corner handle for resizing a selection box.
export class SelectionBoxCorner {
  readonly entityKind = EntityKind.SelectionBoxCorner;

  constructor(
    public parent: SelectionBoxEntity,
    public loc: Point,
    public opposite: Point
  ) {}

  hitTest = (mousePos: MousePos): boolean => {
    return Point.dist(mousePos.sch, this.loc) < 5;
  };

  update = (data: { loc: Point }) => {
    const { loc } = data;
    const rect = { bbox: Bbox.fromPoints(loc, this.opposite) };
    this.parent.update(rect);
    this.loc = loc;
  };
  redraw = (to: DrawTo) => this.parent.redraw(to);
  highlight = (state: boolean) => this.parent.highlight(state);
}
